import { MemberType, ReferenceType } from '@modules/client';

export const eventName = 'RBXScriptSignal';
export const apiReferenceBasePath = '/reference/engine';
export const classesBasePath = `${apiReferenceBasePath}/classes`;
export const datatypesBasePath = `${apiReferenceBasePath}/datatypes`;
export const enumsBasePath = `${apiReferenceBasePath}/enums`;
export const globalsBasePath = `${apiReferenceBasePath}/globals`;
export const librariesBasePath = `${apiReferenceBasePath}/libraries`;

// FIXME: we have an abundance of reference types. clean up & consolidate.
export const engineApiTypeToTranslKey: Record<string, string> = {
  ClassProperty: 'Label.Property',
  DatatypeReference: 'Label.DataType',
  Datatype: 'Label.DataType',
  DatatypeFunction: 'Label.Function',
  DatatypeMethod: 'Label.Method',
  DatatypeConstructor: 'Label.Constructor',
  DatatypeEvent: 'Label.Event',
  DatatypeCallback: 'Label.Callback',
  DatatypeMathOperation: 'Label.MathOperation',
  DatatypeProperty: 'Label.Property',
  ClassReference: 'Label.Class',
  Class: 'Label.Class',
  Enum: 'Label.Enum',
  EnumReference: 'Label.Enum',
  EnumItem: 'Label.Enum',
  GlobalReference: 'Label.Global',
  Global: 'Label.Global',
  GLOBAL: 'Label.Globals', // FIXME: actually links to collection of globals. bad type
  LibraryReference: 'Label.Library',
  LIBRARY: 'Label.Libraries', // FIXME: actually links to collection of libraries. bad type
  Parameter: 'Label.Parameter',
  Return: 'Label.Return',
  Method: 'Label.Method',
  Event: 'Label.Event',
  Property: 'Label.Property',
  Function: 'Label.Function',
  Callback: 'Label.Callback',
  MathOperation: 'Label.MathOperation',
  Constructor: 'Label.Constructor',
};

export const summaryID = 'summary';
export const summaryDashID = 'summary-';
export const propertiesID = 'properties';
export const eventsID = 'events';
export const callbacksID = 'callbacks';
export const functionsID = 'functions';
export const methodsID = 'methods';
export const itemsID = 'items';
export const codeSamplesID = 'code-samples';
export const constructorsID = 'constructors';
export const mathOperationsID = 'math-operations';

export const REGEX_INHERIT = /inherit-[\w]+$/;
export const REGEX_INHERIT_INSTANCE = /inherit-[iI]nstance$/;
export const REGEX_INHERIT_OBJECT = /inherit-[oO]bject$/;

export const referenceTypePathMapping: Record<ReferenceType, string> = {
  [ReferenceType.CLASS]: 'classes',
  [ReferenceType.DATATYPE]: 'datatypes',
  [ReferenceType.ENUM]: 'enums',
  [ReferenceType.GLOBAL]: 'globals',
  [ReferenceType.LIBRARY]: 'libraries',
};

export const referenceTypeLabel: Record<ReferenceType, string> = {
  [ReferenceType.CLASS]: 'Class',
  [ReferenceType.DATATYPE]: 'Data Type',
  [ReferenceType.ENUM]: 'Enum',
  [ReferenceType.GLOBAL]: 'Global',
  [ReferenceType.LIBRARY]: 'Library',
};

/** Maps ReferenceType to the key used in the global References object. */
export const referenceTypeToReferencesKey: Record<
  ReferenceType,
  Exclude<keyof References, 'GlobalByFile' | 'DeprecatedMap'>
> = {
  [ReferenceType.CLASS]: 'Class',
  [ReferenceType.DATATYPE]: 'DataType',
  [ReferenceType.ENUM]: 'Enum',
  [ReferenceType.GLOBAL]: 'Global',
  [ReferenceType.LIBRARY]: 'Library',
};

export const labelTranslKeyByReferenceType: Record<ReferenceType, string> = {
  [ReferenceType.CLASS]: 'Title.Classes',
  [ReferenceType.DATATYPE]: 'Title.DataTypes',
  [ReferenceType.ENUM]: 'Title.Enums',
  [ReferenceType.GLOBAL]: 'Title.Globals',
  [ReferenceType.LIBRARY]: 'Title.Libraries',
};

export const headingTranslKeyByReferenceType: Record<ReferenceType, string> = {
  [ReferenceType.CLASS]: 'Label.EngineClass',
  [ReferenceType.DATATYPE]: 'Label.EngineDataType',
  [ReferenceType.ENUM]: 'Label.EngineEnum',
  [ReferenceType.GLOBAL]: 'Label.EngineGlobal',
  [ReferenceType.LIBRARY]: 'Label.EngineLibrary',
};

export const referenceTypeByTitle: Record<string, ReferenceType> = {
  Classes: ReferenceType.CLASS,
  'Data Types': ReferenceType.DATATYPE,
  Enums: ReferenceType.ENUM,
  Globals: ReferenceType.GLOBAL,
  Libraries: ReferenceType.LIBRARY,
};

export const urlHashByMemberTypeDetailSection: Record<MemberType, string> = {
  [MemberType.Callback]: '#callbacks',
  [MemberType.Constructor]: '#constructors',
  [MemberType.Event]: '#events',
  [MemberType.Function]: '#functions',
  [MemberType.Method]: '#methods',
  [MemberType.Property]: '#properties',
  [MemberType.MathOperation]: '#math-operations',
};

export const labelTranslKeyByMemberType: Record<MemberType, string> = {
  [MemberType.Callback]: 'Label.Callbacks',
  [MemberType.Constructor]: 'Label.Constructors',
  [MemberType.Event]: 'Label.Events',
  [MemberType.Function]: 'Label.Functions',
  [MemberType.Method]: 'Label.Methods',
  [MemberType.Property]: 'Label.Properties',
  [MemberType.MathOperation]: 'Label.MathOperations',
};

// ts type which is one of a list of strings
export const ApiReferenceMemberTypeCollectionHashValues = [
  'callbacks',
  'constructors',
  'events',
  'functions',
  'methods',
  'properties',
  'math-operations',
  'items',
  'constants',
] as const;

export type ApiReferenceMemberTypeCollectionHash =
  (typeof ApiReferenceMemberTypeCollectionHashValues)[number];

export const sectionHashByMemberType: Record<MemberType, ApiReferenceMemberTypeCollectionHash> = {
  [MemberType.Callback]: 'callbacks',
  [MemberType.Constructor]: 'constructors',
  [MemberType.Event]: 'events',
  [MemberType.Function]: 'functions',
  [MemberType.Method]: 'methods',
  [MemberType.Property]: 'properties',
  [MemberType.MathOperation]: 'math-operations',
};

// invert sectionHashByMemberType: { 'callbacks': MemberType.Callback,...}
export const memberTypeBySectionHash: Record<ApiReferenceMemberTypeCollectionHash, MemberType> =
  Object.keys(sectionHashByMemberType).reduce(
    (accum, key) => {
      const k = key as MemberType;
      const v = sectionHashByMemberType[k];
      return {
        ...accum,
        [v]: k,
      };
    },
    {} as Record<ApiReferenceMemberTypeCollectionHash, MemberType>,
  );

export const sectionHashToLabelTranslKey = (sectionHash: ApiReferenceMemberTypeCollectionHash) => {
  const memberType = memberTypeBySectionHash[sectionHash];
  return labelTranslKeyByMemberType[memberType];
};

// construct a reverse map of the above
export const pathReferenceTypeMapping: Record<string, ReferenceType> = Object.values(
  ReferenceType,
).reduce(
  (map, curr) => ({
    ...map,
    [referenceTypePathMapping[curr]]: curr,
  }),
  {},
);

export const memberTypeCollectionsByReferenceType: Record<
  ReferenceType,
  ApiReferenceMemberTypeCollectionHash[]
> = {
  [ReferenceType.CLASS]: ['properties', 'methods', 'events', 'callbacks'],
  [ReferenceType.DATATYPE]: [
    'constructors',
    'constants',
    'properties',
    'methods',
    'functions',
    'math-operations',
  ],
  [ReferenceType.ENUM]: ['items'],
  [ReferenceType.GLOBAL]: ['properties', 'functions'],
  [ReferenceType.LIBRARY]: ['properties', 'functions'],
};
